import { useEffect, useState } from "react";
import closeButton from '../../assets/images/close.png'
import { createStaffEmployee, getStaffEmployees, updateStaffEmployee } from "../../services/AppService";
import { useNavigate } from "react-router-dom";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import ViewEmployeeDetailsModalComponent from "../../components/dashboard/Prceedings/Employees/ViewEmployeeDetailsModalComponent";
import AddStaffEmployeeForm from "../../components/dashboard/Prceedings/Employees/AddStaffEmployeeForm";
import { useTranslation } from "react-i18next";

const Employees = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [employees, setEmployees] = useState([]);
    const [loading, setLoading] = useState(true);

    // Pagination & search states
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [viewModalOpen, setViewModalOpen] = useState(false);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [editingEmployee, setEditingEmployee] = useState(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        formData: null, // Temporary storage for the payload
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: "",
    });

    const showSuccess = (msg) => {
        setMessageModal({
            open: true,
            type: "success",
            title: "Success",
            message: msg,
        });
    };

    const showError = (msg) => {
        setMessageModal({
            open: true,
            type: "error",
            title: "Error",
            message: msg,
        });
    };

    const handleFormSubmit = (formData) => {
        setIsModalOpen(false); // 👈 Close the add form modal first
        setConfirmModal({
            open: true,
            formData: formData,
        });
    };

    // If they cancel the confirmation, you might want to re-open the form
    const handleCancelConfirm = () => {
        setConfirmModal({ open: false, formData: null });
        setIsModalOpen(true); //  Re-open form if they changed their mind
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]);

    useEffect(() => {
        fetchEmployees();
    }, [page, size, debouncedSearch]);

    const fetchEmployees = async () => {
        try {
            setLoading(true);
            const response = await getStaffEmployees(debouncedSearch, page, size);

            // ✅ The fix: Use response.data directly because your JSON doesn't have .content
            if (response) {
                setEmployees(response.content || []);
                // If your backend isn't sending totalPages yet, default to 1
                setTotalPages(response.totalPages || 0);
            }
        } catch (error) {
            console.error("Error fetching staff", error);
            showError("Failed to load employees");
        } finally {
            setLoading(false);
        }
    };

    const handleConfirmSave = async () => {
        const { formData } = confirmModal;
        try {
            let res;
            if (editingEmployee) {
                res = await updateStaffEmployee(editingEmployee.id, formData);
                showSuccess("Member updated successfully");
            } else {
                res = await createStaffEmployee(formData);
                showSuccess("Member added successfully");
            }

            fetchEmployees();
            setConfirmModal({ open: false, formData: null });
            setEditingEmployee(null);
        } catch (error) {
            const backendError = error.response?.data?.message || "Check backend logs for constraints";
            showError(backendError);
        } finally {
            // Close the confirm modal regardless of outcome
            setConfirmModal({ open: false, formData: null });
        }
    };


    const handleAdd = () => {
        setEditingEmployee(null);
        setIsModalOpen(true);
    };


    return (
        <div className="bg-white shadow-lg rounded-2xl p-6">
            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-xl font-semibold text-blue">
                    {t('pageTitles.employeesList')}
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            <div className="text-end mb-2">
                <button className="btnAdd" onClick={handleAdd}>
                    {t('common.addNew')}
                </button>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row justify-between items-center mb-4 gap-3">
                <div className="flex items-center gap-2">
                    <label className="text-sm">{t('common.show')}:</label>
                    <select
                        value={size}
                        onChange={(e) => {
                            setSize(Number(e.target.value));
                            setPage(0); // reset to first page
                        }}
                        className="border rounded px-2 py-1 text-sm"
                    >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                    </select>
                    <label className="text-sm">{t('common.entries')}:</label>
                </div>
                <div>
                    <input
                        type="text"
                        placeholder={t('common.search')}
                        value={search}
                        onChange={(e) => {
                            setSearch(e.target.value);
                            setPage(0); // reset to first page
                        }}
                        className="border rounded px-3 py-1 text-sm"
                    />
                </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                {loading ? (
                    <p className="text-center p-4">{t('tables.employees.loadingEmployees', 'Loading employees...')}</p>
                ) : (
                    <table className="w-full border text-sm ctable rounded-lg">
                        <thead>
                            <tr className="bg-[#a0adf1] text-white">
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.srNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('forms.employee.fullName')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('forms.employee.department')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('forms.employee.designation')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.mobileNo')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.edit')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.view')}</th>
                                <th className="px-3 py-2 text-center border-b border-black">{t('tables.status')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {employees.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="text-center py-4">
                                        {t('tables.employees.noEmployees', 'No employees found')}
                                    </td>
                                </tr>
                            ) : (
                                employees.map((e, idx) => (
                                    <tr
                                        key={e.id}
                                        className={`${idx % 2 === 0 ? "bg-gray-50" : "bg-white"
                                            } hover:bg-gray-100`}
                                    >
                                        <td className="px-3 py-2 text-center">{page * size + idx + 1}</td>
                                        <td className="px-3 py-2">{e.name}</td>
                                        <td className="px-3 py-2">{e.departmentName}</td>
                                        <td className="px-3 py-2">{e.designationName}</td>
                                        <td className="px-3 py-2">{e.contactPrimary}</td>
                                        <td className="px-3 py-2 text-center">
                                            <button className="px-3 py-1 btnImgEdit rounded text-xs"
                                                onClick={() => {
                                                    setEditingEmployee(e);
                                                    setIsModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">
                                            <button
                                                className="px-3 btnImgView py-1 rounded text-xs"
                                                onClick={() => {
                                                    setSelectedEmployee(e);
                                                    setViewModalOpen(true);
                                                }}
                                            ></button>
                                        </td>
                                        <td className="px-3 py-2 text-center">{e.active ? "Active" : "Inactive"}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Footer Info + Pagination */}
            <div className="flex flex-col sm:flex-row justify-between items-center mt-4 text-sm">
                <p>
                    {t('common.showing')} {page * size + 1} {t('common.to')} {page * size + employees.length} {t('common.of')}{" "}
                    {totalPages * size} {t('common.entries')}
                </p>
                <div className="flex gap-2 mt-2 sm:mt-0">
                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.max(p - 1, 0))}
                        disabled={page === 0}
                    >
                        {t('common.previous')}
                    </button>

                    {[...Array(totalPages)].map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setPage(i)}
                            className={`px-3 py-1 border rounded ${page === i
                                ? "bg-gradient-to-b from-white to-[#dcdcdc] border border-[#979797]"
                                : ""
                                }`}
                        >
                            {i + 1}
                        </button>
                    ))}

                    <button
                        className="px-3 py-1 border rounded disabled:opacity-50"
                        onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
                        disabled={page === totalPages - 1}
                    >
                        {t('common.next')}
                    </button>
                </div>
            </div>

            <AddStaffEmployeeForm onSubmit={handleFormSubmit}
                editData={editingEmployee}
                open={isModalOpen}
                onClose={() => { setIsModalOpen(false); setEditingEmployee(null); }}
            />


            <ConfirmActionModal
                open={confirmModal.open}
                title={editingEmployee ? "Confirm Update" : "Confirm Addition"}
                message={
                    editingEmployee
                        ? "Are you sure you want to update this employee's details?"
                        : "Are you sure you want to add this new employee to the system?"
                }
                confirmText={editingEmployee ? "UPDATE" : "SAVE"}
                onConfirm={handleConfirmSave}
                onClose={handleCancelConfirm}
            />

            {/* FEEDBACK MODAL */}
            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                type={messageModal.type}
                title={messageModal.title}
                message={messageModal.message}
            />

            {/* View Modal */}
            <ViewEmployeeDetailsModalComponent
                isOpen={viewModalOpen}
                onClose={() => setViewModalOpen(false)}
                employee={selectedEmployee}
            />

        </div>
    )
}

export default Employees;